import { useState } from "react";
import { useTranslation } from "react-i18next";

import { FilterBar } from "@/components/filter-bar";
import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminUsers } from "@/lib/api";
import { roleLabel } from "@/lib/roles";

import { UserTable } from "./user-table";
import {
  filterUsers,
  hasUserFilters,
  isRoleFilter,
  NO_USER_FILTERS,
  ROLE_FILTERS,
  type UserFilters,
} from "./utils";

/**
 * Mensen en teams → Mensen (admins, `/people`): who can sign in to this
 * dashboard, and their roles, in one table with a search on name and email
 * and a role filter above it, and no intro: the tab and the table say what it
 * is. Nobody is invited: a colleague's account appears at their first
 * Microsoft sign-in, as the note under the table says.
 */
export function UsersPage() {
  const { t } = useTranslation();
  const usersQuery = useAdminUsers();
  const [filters, setFilters] = useState<UserFilters>(NO_USER_FILTERS);
  const filtered = hasUserFilters(filters);
  const reset = () => setFilters(NO_USER_FILTERS);

  return usersQuery.isLoading ? (
    <LoadingCard />
  ) : usersQuery.isError ? (
    <LoadErrorCard message={t("users.loadError")} onRetry={() => void usersQuery.refetch()} />
  ) : (
    <div className="space-y-4">
      <FilterBar
        search={{
          value: filters.search,
          onChange: (search) => setFilters({ ...filters, search }),
          label: t("users.search"),
        }}
        columns="narrow"
        filtered={filtered}
        onReset={reset}
      >
        <Select
          value={filters.role}
          onValueChange={(role) => isRoleFilter(role) && setFilters({ ...filters, role })}
        >
          <SelectTrigger size="sm" aria-label={t("settings.role")} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLE_FILTERS.map((role) => (
              <SelectItem key={role} value={role}>
                {role === "all" ? t("users.allRoles") : roleLabel(role)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterBar>
      <UserTable
        users={filterUsers(usersQuery.data ?? [], filters)}
        search={filters.search}
        onReset={filtered ? reset : undefined}
      />
      <p className="text-muted text-sm">{t("users.firstSignIn")}</p>
    </div>
  );
}
